/**
 * POS HTTP — keep all `/storebusiness/.../pos/...` calls here so this feature can move to another repo.
 */
import { isAxiosError } from 'axios'
import { api } from '@/services/api'

function assertSuccess(body: unknown) {
  const b = body as { success?: boolean; message?: string; error?: { code?: string }; data?: unknown }
  if (!b?.success) {
    const err = new Error(b?.message || 'Request failed') as Error & { code?: string }
    err.code = b.error?.code
    throw err
  }
  return b.data
}

export type PosPaymentMethod = 'cash' | 'transfer'

export type PosCreateSaleBody = {
  items: Array<{ productId: number; quantity: number }>
  paymentMethod: PosPaymentMethod
  idempotencyKey: string
  recordedAtDevice?: string
}

export type PosCreateSaleResult = {
  replayed: boolean
  saleReference: string
  id: number
  total: string
  subtotal: string
  taxTotal: string
  currency: string
  paymentMethod: string
  createdAt: string
}

export async function createPosSale(storeId: number, body: PosCreateSaleBody): Promise<PosCreateSaleResult> {
  try {
    const { data } = await api.post(`/storebusiness/stores/${encodeURIComponent(storeId)}/pos/sales`, body)
    return assertSuccess(data) as PosCreateSaleResult
  } catch (e) {
    if (isAxiosError(e) && e.response?.data && typeof e.response.data === 'object') {
      const payload = e.response.data as { message?: string; error?: { code?: string; details?: unknown } }
      const err = new Error(payload.message || 'Request failed') as Error & {
        code?: string
        status?: number
        details?: unknown
      }
      err.code = payload.error?.code
      err.status = e.response.status
      err.details = payload.error?.details
      throw err
    }
    throw e
  }
}

export async function listPosSales(
  storeId: number,
  params: { page?: number; limit?: number; from?: string; to?: string } = {},
) {
  const { data } = await api.get(`/storebusiness/stores/${encodeURIComponent(storeId)}/pos/sales`, {
    params,
  })
  return assertSuccess(data) as {
    items: Array<{
      id: number
      saleReference: string
      paymentMethod: string
      currency: string
      subtotal: string
      taxTotal: string
      total: string
      createdAt: string
      staff: { id: number; firstName: string; lastName: string; email: string } | null
      lines: Array<{
        id: number
        quantity: number
        unitSalePrice: string
        lineTotal: string
        productName: string
        productSlug: string
      }>
    }>
    total: number
    page: number
    limit: number
  }
}
